import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';

interface AuthContextValue {
  sessionToken: string | null;
  idContact: number | null;
  idUser: number | null;
  role: string | null;
  scope: string | null;
  isAuthenticated: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const readNumber = (key: string): number | null => {
  const value = localStorage.getItem(key);
  if (value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Source of truth bleibt vorerst localStorage — der Provider kapselt die Reads.
  // Werte werden nur einmal pro Mount gelesen; nach Logout wird per reload() neu
  // initialisiert, daher reicht das.
  const sessionToken = localStorage.getItem('SessionToken');
  const idContact = readNumber('idContact');
  const idUser = readNumber('idUser');
  const role = localStorage.getItem('role');
  const scope = localStorage.getItem('scope');

  const logout = useCallback(() => {
    localStorage.removeItem('SessionToken');
    localStorage.removeItem('TimestampSessionToken');
    localStorage.removeItem('idContact');
    localStorage.removeItem('idUser');
    localStorage.removeItem('role');
    localStorage.removeItem('scope');
    localStorage.removeItem('passwordResetRequired');
    window.location.reload();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      sessionToken,
      idContact,
      idUser,
      role,
      scope,
      isAuthenticated: sessionToken !== null,
      logout,
    }),
    [sessionToken, idContact, idUser, role, scope, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};
